import { Button, Form, Header, Input, Text } from '@gasbuddy/react-components';
import { useTranslation, Trans } from 'react-i18next';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { goTo } from '../../../lib/goTo';
import useAnalyticsContext from '../../../lib/hooks/useAnalyticsContext';
import ANALYTICS_EVENTS from '../../constants/analyticsEvents';
import { LoginStatus } from '../../reducers/login';
import { MagicLinkStatus } from '../../reducers/magiclinks';
import SupportLink from '../SupportLink';
import styles from './LoginCodeEntry.module.css';

const cx = classnames.bind(styles);

export default function LoginCodeEntry({
  history,
  destination,
  loginStatus,
  errorMessage,
  variant,
  identifierIsUsername,
  deviceLabel,
  redeemLoginCode,
}) {
  const { t } = useTranslation(['auth', 'errors', 'miscellaneous']);
  const [code, setCode] = useState();
  const [codeError, setCodeError] = useState();
  const analytics = useAnalyticsContext();

  const onCodeEntered = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!code || code.trim().length === 0) {
      setCodeError(t('login.code.required', { ns: 'errors' }));
      return;
    }
    if (code.trim().length !== 6) {
      setCodeError(t('login.code.invalid', { ns: 'errors' }));
      return;
    }
    analytics.tagEvent({ name: ANALYTICS_EVENTS.IAM_Login_CodeEntered });
    await redeemLoginCode(code, history.location.search);
  }, [analytics, code, history.location.search, redeemLoginCode, t]);

  useEffect(() => {
    if (loginStatus === LoginStatus.LoggedIn && typeof window !== 'undefined') {
      analytics.tagEvent({ name: ANALYTICS_EVENTS.IAM_User_Successfully_Logged_In });
      goTo(destination);
    }
  }, [analytics, destination, loginStatus]);

  const onIdentifierChange = useCallback(({ target }) => {
    setCode(target.value);
    setCodeError(undefined);
  }, []);

  const emailSentString = identifierIsUsername
    ? t('loginCode.sent.emailTargetUsername')
    : t('loginCode.sent.emailTargetDirect');

  switch (variant) {
    case MagicLinkStatus.Sent: {
      return (
        <React.Fragment>
          <Header as="h3">{t('loginCode.sent.title')}</Header>
          <Form action={history.location.pathname} method="POST" onSubmit={onCodeEntered}>
            <Text as="p">
              <Trans
                i18nKey="loginCode.sent.message"
                values={{ emailTarget: emailSentString }}
              >
                We&apos;ve sent a code to {{ emailSentString }}. <br />
                The code will expire within the next <strong>30 minutes</strong>.
              </Trans>
            </Text>
            <br />
            <Input
              id="code"
              name="code"
              data-test="codeInput"
              label={t('loginCode.form.label')}
              isValid={code && code.length === 6}
              error={codeError || errorMessage}
              value={code}
              onChange={onIdentifierChange}
              autoComplete="off"
              className={cx('codeInput', { filled: !!code?.length })}
            />
            <Text as="p" bold>
              <Trans i18nKey="loginCode.sent.support">
                If you don&apos;t receive your login code, <SupportLink>contact support</SupportLink>.
              </Trans>
            </Text>
            <br />
            <Button
              primary
              cta
              type="submit"
              loading={loginStatus === LoginStatus.InProgress}
              wide
            >
              {t('actions.login', { ns: 'miscellaneous' })}
            </Button>
          </Form>
        </React.Fragment>
      );
    }

    // TODO: There is nothing that causes this status to be set
    case MagicLinkStatus.Throttled: {
      return (
        <React.Fragment>
          <Header as="h3">{t('loginCode.throttled.title')}</Header>
          <Text as="p">
            <Trans
              i18nKey="loginCode.throttled.message"
              values={{ emailTarget: emailSentString }}
            >
              We&apos;ve recently sent a code to {{ emailSentString }}.
              You can try again in <strong>5 minutes.</strong>
            </Trans>
          </Text>
          <Text as="p" bold>
            <Trans i18nKey="loginCode.throttled.support">
              If you don&apos;t receive your login code, <SupportLink>contact support</SupportLink>.
            </Trans>
          </Text>
        </React.Fragment>
      );
    }

    case MagicLinkStatus.InvalidDevice: {
      return (
        <React.Fragment>
          <Header as="h3">{t('loginCode.invalidDevice.title', { ns: 'errors' })}</Header>
          <Text as="p">
            {t('loginCode.invalidDevice.message', { ns: 'errors' })} <br />
          </Text>
          <Text as="p" bold>{deviceLabel}</Text>
          <Text as="p">
            {t('loginCode.invalidDevice.alternative', { ns: 'errors' })}
          </Text>
        </React.Fragment>
      );
    }

    case MagicLinkStatus.InvalidCode: {
      return (
        <React.Fragment>
          <Header as="h3">{t('loginCode.invalidDevice.title', { ns: 'errors' })}</Header>
          <Text as="p">
            {t('loginCode.invalidCode.message', { ns: 'errors' })}
          </Text>
          <Text as="p">
            <Trans i18nKey="loginCode.invalidCode.instruction" ns="errors">
              To log in, request another code and be sure to use it within <Text bold>30 minutes</Text> of receiving the email.
            </Trans>
          </Text>
        </React.Fragment>
      );
    }

    default: {
      return false;
    }
  }
}

LoginCodeEntry.propTypes = {
  variant: PropTypes.number,
  loginStatus: PropTypes.number,
  identifierIsUsername: PropTypes.bool,
  deviceLabel: PropTypes.string,
  destination: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  redeemLoginCode: PropTypes.func,
  history: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
    }),
  }),
};

LoginCodeEntry.defaultProps = {
  variant: undefined,
  loginStatus: undefined,
  identifierIsUsername: false,
  deviceLabel: '',
  errorMessage: undefined,
  redeemLoginCode: () => {},
  history: {
    location: {
      pathname: '/',
      search: '',
    },
  },
};
